import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, LayersControl, Polygon as LPolygon, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ZONE_META, CATEGORY_META, SEVERITY_META } from '@/lib/format';

// Shim default marker icon (leaflet needs static images)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// SVG icon factory - returns divIcon (fully custom styling)
function svgIcon({ color, symbol, size = 26, ring = null, pulse = false }) {
  const ringStroke = ring || color;
  return L.divIcon({
    html: `
      <div class="ad-map-icon${pulse ? ' ad-map-icon-pulse' : ''}" style="--ic-color:${color}">
        <svg viewBox="0 0 32 32" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="13" fill="white" stroke="${ringStroke}" stroke-width="2"></circle>
          ${symbol}
        </svg>
      </div>`,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const SYM = {
  dam: `<rect x="10" y="9" width="12" height="14" rx="1" fill="#1FB6A6"/><path d="M10 15h12M10 19h12" stroke="white" stroke-width="1"/>`,
  powerhouse: `<rect x="10" y="12" width="12" height="10" fill="#0EA5E9"/><polygon points="10,12 16,7 22,12" fill="#0EA5E9"/>`,
  reservoir: `<circle cx="16" cy="16" r="6" fill="#3B82F6"/>`,
  spillway: `<path d="M10 22 L14 12 L18 22 L22 12" stroke="#0EA5E9" stroke-width="2" fill="none"/>`,
  intake: `<path d="M11 22 L16 10 L21 22 Z" fill="#0EA5E9"/>`,
  tailrace: `<path d="M10 16 Q16 22 22 16" stroke="#0EA5E9" stroke-width="2" fill="none"/>`,
  switchyard: `<path d="M13 9 L16 14 L13 14 L18 22 L15 17 L18 17 L16 9 Z" fill="#F59E0B"/>`,
  colony: `<path d="M10 22 L10 15 L16 10 L22 15 L22 22 Z" fill="#94A3B8"/>`,
  landslide: `<path d="M10 22 L14 12 L18 18 L22 8" stroke="#F97316" stroke-width="2.5" fill="none" stroke-linecap="round"/><circle cx="18" cy="18" r="2" fill="#F97316"/>`,
  encroachment: `<rect x="10" y="14" width="4" height="8" fill="#A855F7"/><rect x="15" y="11" width="4" height="11" fill="#A855F7"/><rect x="20" y="16" width="4" height="6" fill="#A855F7"/>`,
  water: `<path d="M10 15 Q13 12 16 15 T22 15 M10 19 Q13 16 16 19 T22 19" stroke="#3B82F6" stroke-width="1.8" fill="none"/>`,
  fire: `<path d="M16 8 C 12 12 12 15 14 16 C 12 17 11 19 12 21 C 13 23 15 23 17 22 C 20 23 22 21 22 18 C 22 15 18 15 16 8 Z" fill="#EF4444"/>`,
  flood: `<path d="M9 18 Q13 15 16 18 T23 18" stroke="#3B82F6" stroke-width="2.5" fill="none"/><path d="M9 22 Q13 19 16 22 T23 22" stroke="#3B82F6" stroke-width="2.5" fill="none"/>`,
  incident: `<path d="M16 8 L22 22 L10 22 Z" fill="#EF4444"/><rect x="15" y="13" width="2" height="5" fill="white"/><rect x="15" y="19" width="2" height="2" fill="white"/>`,
  incident_closed: `<path d="M12 16 L15 19 L21 12" stroke="#22C55E" stroke-width="3" fill="none" stroke-linecap="round"/>`,
  field: `<circle cx="16" cy="15" r="4" fill="#3B82F6"/><path d="M16 19 L16 24" stroke="#3B82F6" stroke-width="2"/>`,
  observation: `<circle cx="16" cy="16" r="4" fill="#F59E0B"/><circle cx="16" cy="16" r="7" stroke="#F59E0B" stroke-width="1" fill="none" opacity="0.5"/>`,
};

function iconForSubAsset(kind) {
  return svgIcon({ color: '#1FB6A6', symbol: SYM[kind] || SYM.dam, size: 30 });
}

function iconForAlert({ category, severity, isIncident, isClosed }) {
  const sevColor = SEVERITY_META[severity]?.hex || '#F59E0B';
  if (isClosed) {
    return svgIcon({ color: '#22C55E', ring: '#22C55E', symbol: SYM.incident_closed, size: 28 });
  }
  if (isIncident) {
    return svgIcon({ color: sevColor, ring: sevColor, symbol: SYM.incident, size: 32, pulse: severity === 'Critical' });
  }
  const sym = SYM[category === 'terrain_disturbance' ? 'landslide'
    : category === 'encroachment' ? 'encroachment'
    : category === 'water_change' ? 'water'
    : category === 'fire_flood' ? 'fire' : 'observation'];
  return svgIcon({ color: sevColor, ring: sevColor, symbol: sym, size: 30, pulse: severity === 'Critical' });
}

function iconForObservation() {
  return svgIcon({ color: '#F59E0B', symbol: SYM.observation, size: 26 });
}

function RecenterOnAsset({ center, zoom }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, zoom || map.getZoom() || 13); }, [center, zoom, map]);
  return null;
}

/**
 * AssetMap with layered markers:
 *  - Asset boundary (polygon)
 *  - Monitoring zones (7 distinct polygons)
 *  - Sub-assets (dam, powerhouse, reservoir, etc.)
 *  - Alerts / Incidents by category (distinct icons + severity colour)
 *  - Optional fire radius / flood polygon overlays
 */
export function AssetMap({
  asset, zones = [], subAssets = [], alerts = [], incidents = [],
  observations = [], onMarkerClick, height = 520, defaultZoom = 13, showLegend = true,
}) {
  const center = useMemo(() => [asset?.lat || 30.20, asset?.lng || 78.55], [asset]);

  return (
    <div className="rounded-xl overflow-hidden border border-border relative" style={{ height }} data-testid="asset-map">
      <MapContainer center={center} zoom={defaultZoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Satellite (ESRI)">
            <TileLayer
              attribution="Tiles &copy; Esri"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Streets (OSM)">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Terrain">
            <TileLayer
              attribution='Map data &copy; OpenTopoMap'
              url="https://{s}.tile.opentopomap.org/{z}/{y}/{x}.png"
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        <RecenterOnAsset center={center} zoom={defaultZoom} />

        {/* Asset boundary */}
        {asset?.boundary?.coords?.length > 2 && (
          <LPolygon positions={asset.boundary.coords}
                    pathOptions={{ color: '#1FB6A6', weight: 2, fillOpacity: 0.04, dashArray: '4 4' }} />
        )}

        {/* Zones */}
        {zones.map((z) => {
          if (!z.boundary?.coords?.length) return null;
          const meta = ZONE_META[z.zone_type] || { color: '#94a3b8', dash: '0' };
          return (
            <LPolygon key={z.id} positions={z.boundary.coords}
              pathOptions={{
                color: meta.color, weight: 1.5, fillColor: meta.color, fillOpacity: 0.08,
                dashArray: meta.dash === '0' ? undefined : meta.dash,
              }}>
              <Popup>
                <div className="text-xs">
                  <div className="font-semibold">{z.name}</div>
                  <div className="text-slate-500 uppercase tracking-wide">{meta.label} zone</div>
                  <div>Risk: {z.risk_category}</div>
                </div>
              </Popup>
            </LPolygon>
          );
        })}

        {/* Sub-assets */}
        {subAssets.map((s, i) => (
          <Marker key={`sub-${i}`} position={[s.lat, s.lng]} icon={iconForSubAsset(s.kind)}>
            <Popup>
              <div className="text-xs">
                <div className="font-semibold">{s.name}</div>
                <div className="text-slate-500">{s.description}</div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Observations (as a subtle layer if present) */}
        {observations.map((o) => o.change_lat && (
          <Marker key={`obs-${o.id}`} position={[o.change_lat, o.change_lng]} icon={iconForObservation()}>
            <Popup>
              <div className="text-xs">
                <div className="font-semibold">AI observation</div>
                <div className="text-slate-500">{o.change_type}</div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Alerts */}
        {alerts.filter(a => a.change_lat).map((a) => (
          <React.Fragment key={a.id}>
            <Marker
              position={[a.change_lat, a.change_lng]}
              icon={iconForAlert({ category: a.category, severity: a.severity, isIncident: !!a.incident_id })}
              eventHandlers={{ click: () => onMarkerClick?.({ kind: 'alert', ...a }) }}
            >
              <Popup>
                <div className="text-xs" style={{ minWidth: 220 }}>
                  <div className="font-semibold">{a.title}</div>
                  <div className="text-slate-500 uppercase tracking-wider mt-0.5">{a.severity} · {a.status}</div>
                  <div className="mt-1">Risk {a.risk_score} · Conf {Math.round((a.confidence || 0) * 100)}%</div>
                </div>
              </Popup>
            </Marker>
            {/* Category-specific extras */}
            {a.category === 'fire_flood' && (
              <Circle center={[a.change_lat, a.change_lng]} radius={250}
                      pathOptions={{ color: '#EF4444', fillColor: '#EF4444', fillOpacity: 0.08, weight: 1 }} />
            )}
          </React.Fragment>
        ))}

        {/* Incidents that have a location and are still open */}
        {incidents.filter(i => i.change_lat).map((i) => (
          <Marker key={`inc-${i.id}`} position={[i.change_lat, i.change_lng]}
                  icon={iconForAlert({ severity: i.severity, isIncident: true,
                                       isClosed: ['Closed', 'Resolved'].includes(i.status) })}>
            <Popup>
              <div className="text-xs">
                <div className="font-mono">{i.number}</div>
                <div className="font-semibold">{i.title}</div>
                <div className="text-slate-500">{i.status}</div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {showLegend && (
        <div className="absolute bottom-3 left-3 z-[1000] bg-background/95 border border-border rounded-lg p-2 text-[10px] leading-tight shadow-lg backdrop-blur">
          <div className="uppercase tracking-widest text-muted-foreground mb-1">Legend</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 min-w-[220px]">
            <LegendRow color="#1FB6A6" label="Core zone / Asset boundary" />
            <LegendRow color="#3B82F6" label="Protection buffer" />
            <LegendRow color="#22C55E" label="Catchment" />
            <LegendRow color="#F2B705" label="Downstream" />
            <LegendRow color="#64748B" label="Access corridor" />
            <LegendRow color="#F97316" label="Slope zone" />
            <LegendRow color="#EF4444" label="Restricted zone" />
            <LegendRow color="#EF4444" label="Alert / Incident" />
          </div>
        </div>
      )}
    </div>
  );
}

function LegendRow({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-sm" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}
